/**
 * DistributionPackage — one-click magic-link distribution UX.
 *
 * Replaces the raw URL list dumps with a focused panel:
 *   • headline summary of how many links are ready
 *   • a single "Copy all as message" action that emits a per-recipient
 *     formatted block HR can paste straight into email / Slack / WA
 *   • individual URLs hidden by default; expandable when HR wants to
 *     hand-edit or spot-check
 *
 * Phase-1 keeps distribution manual; this component just makes the
 * manual step low-friction. Phase-2 will replace it with auto-send.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where, documentId, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import Button from '../ui/Button';
import { useToast } from '../ui/Toast';
import { sendMagicLinkEmail, sendMagicLinkEmailsBulk } from '../../services/emailService';

/** Resend-derived delivery state, mirrored from MagicLinkToken. */
type DeliveryStatus =
  | 'sent' | 'delivered' | 'opened' | 'clicked'
  | 'bounced' | 'complained' | 'delayed';

/** UI mapping for the badge — icon, color, human-readable label. */
const DELIVERY_BADGE: Record<DeliveryStatus, { icon: string; label: string; cls: string }> = {
  sent:       { icon: 'mail',           label: 'Sent',       cls: 'border-stone-300 bg-stone-50 text-stone-700' },
  delivered:  { icon: 'mark_email_read',label: 'Delivered',  cls: 'border-blue-300 bg-blue-50 text-blue-800' },
  opened:     { icon: 'drafts',         label: 'Opened',     cls: 'border-amber-300 bg-amber-50 text-amber-800' },
  clicked:    { icon: 'ads_click',      label: 'Clicked',    cls: 'border-emerald-300 bg-emerald-50 text-emerald-800' },
  bounced:    { icon: 'error',          label: 'Bounced',    cls: 'border-red-300 bg-red-50 text-red-800' },
  complained: { icon: 'report',         label: 'Spam-flagged', cls: 'border-red-300 bg-red-50 text-red-800' },
  delayed:    { icon: 'hourglass_top',  label: 'Delayed',    cls: 'border-amber-300 bg-amber-50 text-amber-800' },
};

/** Firestore `in` clause maxes at 30 — chunk tokenIds before subscribing. */
const FIRESTORE_IN_LIMIT = 30;
function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export interface DistributionLink {
  /** Recipient email — used to group entries per person. */
  recipientEmail: string;
  recipientName?: string;
  /** Short label of what this link is for, e.g. "EPR self-assessment". */
  action: string;
  /** Free-form context, e.g. "for Andi (peer review)". */
  context?: string;
  url: string;
  /**
   * Magic-link token id. When present, enables one-click "Send email"
   * via the sendMagicLinkEmail Cloud Function (Resend behind the
   * scenes). When omitted, the row falls back to copy-only.
   */
  tokenId?: string;
}

export interface DistributionPackageProps {
  title?: string;
  links: DistributionLink[];
  /** Cycle name used to compose the message header. */
  cycleName?: string;
  /**
   * Pre-populated per-tokenId send status, used when the parent has
   * already fired a bulk send (e.g. on Launch Step 1 submit) and wants
   * each row to display ✓ Sent / ✗ Failed without re-running the call.
   * Failed rows still expose a "Resend" button — successful rows show
   * a confirmation chip and disable the per-row Send.
   */
  initialSendStatus?: Record<string, 'sent' | 'error'>;
}

const DistributionPackage: React.FC<DistributionPackageProps> = ({
  title = 'Links ready for distribution',
  links,
  cycleName,
  initialSendStatus,
}) => {
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState<'all' | 'urls' | null>(null);
  // Map of tokenId → 'sending' | 'sent' | 'error' for per-row UI state.
  // Seeded from initialSendStatus so the parent's bulk-send result is
  // immediately reflected; subsequent per-row Resend clicks layer on top.
  const [sendState, setSendState] = useState<Record<string, 'sending' | 'sent' | 'error'>>(
    initialSendStatus ?? {},
  );
  const [bulkSending, setBulkSending] = useState(false);

  // If the parent updates initialSendStatus later (e.g. a retry batch),
  // merge it into the local state. Local 'sending' transitions still win
  // so we don't blink a row from sending back to sent prematurely.
  React.useEffect(() => {
    if (!initialSendStatus) return;
    setSendState(prev => {
      const next = { ...prev };
      for (const [tokenId, status] of Object.entries(initialSendStatus)) {
        if (prev[tokenId] !== 'sending') next[tokenId] = status;
      }
      return next;
    });
  }, [initialSendStatus]);

  // Live delivery state per tokenId, fed by the resendWebhook function.
  // We subscribe to the magicLinkTokens docs in real time so HR sees
  // status change without refreshing — token gets stamped → snapshot
  // fires → badge updates within ~1 second of Resend's webhook hitting.
  const [deliveryStatus, setDeliveryStatus] = useState<Record<string, DeliveryStatus>>({});
  const tokenIds = useMemo(
    () => links.map(l => l.tokenId).filter((t): t is string => Boolean(t)),
    [links],
  );
  useEffect(() => {
    if (tokenIds.length === 0) return;
    // Firestore `in` queries cap at 30 — chunk for larger batches.
    const unsubscribers = chunk(tokenIds, FIRESTORE_IN_LIMIT).map(group => {
      const q = query(
        collection(db, 'magicLinkTokens'),
        where(documentId(), 'in', group),
      );
      return onSnapshot(q, snap => {
        setDeliveryStatus(prev => {
          const next = { ...prev };
          snap.docs.forEach(d => {
            const s = (d.data() as { emailDeliveryStatus?: DeliveryStatus }).emailDeliveryStatus;
            if (s) next[d.id] = s;
          });
          return next;
        });
      }, () => {
        // Permission errors are non-fatal — just leave the badge blank.
        // (Cross-project deploys can briefly mismatch rules during cutover.)
      });
    });
    return () => unsubscribers.forEach(u => u());
  }, [tokenIds]);

  const grouped = useMemo(() => groupByRecipient(links), [links]);

  if (links.length === 0) return null;

  const messageText = buildMessageText(grouped, cycleName);
  const urlListText = links.map(l => l.url).join('\n');
  const sendableTokens = links.map(l => l.tokenId).filter((t): t is string => Boolean(t));
  const canSendEmails = sendableTokens.length > 0;

  const copyAll = async () => {
    try { await navigator.clipboard.writeText(messageText); setCopied('all'); setTimeout(() => setCopied(null), 2000); }
    catch { /* ignore */ }
  };

  const copyUrlsOnly = async () => {
    try { await navigator.clipboard.writeText(urlListText); setCopied('urls'); setTimeout(() => setCopied(null), 2000); }
    catch { /* ignore */ }
  };

  const sendOne = async (tokenId: string) => {
    setSendState(s => ({ ...s, [tokenId]: 'sending' }));
    try {
      const r = await sendMagicLinkEmail({ tokenId });
      setSendState(s => ({ ...s, [tokenId]: 'sent' }));
      toast.show(
        r.status === 'already_sent'
          ? `Email was already sent to ${r.to}.`
          : `Email sent to ${r.to}.`,
        { icon: 'check' },
      );
    } catch (e) {
      setSendState(s => ({ ...s, [tokenId]: 'error' }));
      toast.show(`Send failed: ${(e as Error).message}`, { icon: 'error' });
    }
  };

  const sendAll = async () => {
    setBulkSending(true);
    sendableTokens.forEach(t => setSendState(s => ({ ...s, [t]: 'sending' })));
    try {
      const results = await sendMagicLinkEmailsBulk(sendableTokens);
      const next: Record<string, 'sending' | 'sent' | 'error'> = {};
      let okCount = 0;
      let failCount = 0;
      for (const r of results) {
        if (r.ok) { next[r.tokenId] = 'sent'; okCount += 1; }
        else      { next[r.tokenId] = 'error'; failCount += 1; }
      }
      setSendState(s => ({ ...s, ...next }));
      toast.show(
        failCount === 0
          ? `Sent ${okCount} email${okCount === 1 ? '' : 's'}.`
          : `Sent ${okCount}, failed ${failCount}.`,
        { icon: failCount === 0 ? 'check' : 'warning' },
      );
    } finally {
      setBulkSending(false);
    }
  };

  return (
    <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mt-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="material-symbols-rounded text-emerald-600" style={{ fontSize: 18 }}>send</span>
            <h4 className="text-sm font-semibold text-emerald-900">{title}</h4>
          </div>
          <div className="text-xs text-emerald-800 mt-0.5">
            <b>{links.length}</b> link{links.length !== 1 ? 's' : ''} · <b>{grouped.length}</b> recipient{grouped.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canSendEmails && (
            <Button
              variant="submit"
              icon="mail"
              loading={bulkSending}
              onClick={sendAll}
            >
              Send {sendableTokens.length} email{sendableTokens.length !== 1 ? 's' : ''}
            </Button>
          )}
          <Button
            variant={canSendEmails ? 'tertiary' : 'submit'}
            icon={copied === 'all' ? 'check' : 'content_copy'}
            onClick={copyAll}
          >
            {copied === 'all' ? 'Copied' : 'Copy as message'}
          </Button>
          <Button
            variant="tertiary"
            icon={expanded ? 'expand_less' : 'expand_more'}
            onClick={() => setExpanded(e => !e)}
          >
            {expanded ? 'Hide details' : 'Show details'}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-emerald-200 space-y-3">
          {/* Per-recipient view */}
          <div className="space-y-2">
            {grouped.map(g => (
              <div key={g.email} className="rounded-lg bg-white border border-emerald-100 p-3">
                <div className="text-xs text-emerald-900 font-medium mb-1">
                  {g.name ? <>{g.name} <span className="text-emerald-700 font-normal">· {g.email}</span></> : g.email}
                </div>
                <ul className="space-y-1.5">
                  {g.items.map((it, idx) => {
                    const rowState = it.tokenId ? sendState[it.tokenId] : undefined;
                    const delivery = it.tokenId ? deliveryStatus[it.tokenId] : undefined;
                    const deliveryUi = delivery ? DELIVERY_BADGE[delivery] : null;
                    return (
                      <li key={idx} className="flex items-start gap-2 text-xs">
                        <span className="text-emerald-700 font-medium mt-0.5">{idx + 1}.</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-emerald-900">
                            {it.action}{it.context ? ` · ${it.context}` : ''}
                          </div>
                          <code className="text-[10px] text-stone-600 break-all">{it.url}</code>
                        </div>
                        {/* Live delivery state — fed by resendWebhook.
                            Shown only after an event has been recorded (so
                            a row with no badge means "either not sent yet,
                            or webhook not configured yet"). */}
                        {deliveryUi && (
                          <span
                            className={[
                              'text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 flex-shrink-0',
                              deliveryUi.cls,
                            ].join(' ')}
                            title={`Delivery: ${deliveryUi.label}`}
                          >
                            <span className="material-symbols-rounded" style={{ fontSize: 11 }} aria-hidden="true">
                              {deliveryUi.icon}
                            </span>
                            {deliveryUi.label}
                          </span>
                        )}
                        {it.tokenId && (
                          <button
                            type="button"
                            className={[
                              'text-xs px-2 py-0.5 rounded border flex-shrink-0 inline-flex items-center gap-1',
                              rowState === 'sent'
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                                : rowState === 'error'
                                  ? 'border-red-300 bg-red-50 text-red-800'
                                  : 'border-stone-300 bg-white hover:bg-stone-50',
                            ].join(' ')}
                            disabled={rowState === 'sending'}
                            onClick={() => sendOne(it.tokenId!)}
                          >
                            <span className="material-symbols-rounded" style={{ fontSize: 12 }}>
                              {rowState === 'sending' ? 'sync' : rowState === 'sent' ? 'check' : 'mail'}
                            </span>
                            {rowState === 'sent' ? 'Sent' : rowState === 'sending' ? 'Sending' : 'Send'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="text-xs px-2 py-0.5 rounded border border-stone-300 bg-white hover:bg-stone-50 flex-shrink-0"
                          onClick={() => navigator.clipboard.writeText(it.url).catch(() => undefined)}
                        >
                          Copy
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-emerald-200">
            <div className="text-xs text-emerald-800">
              Need just the URLs? Use the bare-URL clipboard:
            </div>
            <Button
              variant="tertiary"
              icon={copied === 'urls' ? 'check' : 'link'}
              onClick={copyUrlsOnly}
            >
              {copied === 'urls' ? 'Copied' : 'Copy URLs only'}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────

interface GroupedRecipient {
  email: string;
  name?: string;
  items: Array<{ action: string; context?: string; url: string; tokenId?: string }>;
}

function groupByRecipient(links: DistributionLink[]): GroupedRecipient[] {
  const map = new Map<string, GroupedRecipient>();
  for (const l of links) {
    const key = l.recipientEmail.toLowerCase().trim();
    if (!map.has(key)) {
      map.set(key, { email: l.recipientEmail, name: l.recipientName, items: [] });
    }
    map.get(key)!.items.push({
      action: l.action, context: l.context, url: l.url, tokenId: l.tokenId,
    });
  }
  return Array.from(map.values());
}

function buildMessageText(grouped: GroupedRecipient[], cycleName?: string): string {
  const lines: string[] = [];
  const header = cycleName
    ? `Performance review links — ${cycleName}`
    : 'Performance review links';
  lines.push(header);
  lines.push('='.repeat(Math.min(header.length, 60)));
  lines.push('');
  for (const g of grouped) {
    lines.push(g.name ? `--- ${g.name} (${g.email}) ---` : `--- ${g.email} ---`);
    g.items.forEach((it, idx) => {
      const prefix = `${idx + 1}. `;
      lines.push(`${prefix}${it.action}${it.context ? ` · ${it.context}` : ''}`);
      lines.push(`   ${it.url}`);
    });
    lines.push('');
  }
  lines.push('Tokens are single-use unless marked otherwise. Default expiry: 15 days.');
  return lines.join('\n');
}

export default DistributionPackage;
